import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { filter, take } from 'rxjs/operators';
import { WebsocketService } from '../../services/websocket.service';
import { TranslatePipe } from '@ngx-translate/core';

/**
 * The page a friend lands on from a share link (/share/:token). The whole app is behind
 * Keycloak login, so by the time this renders the user is authenticated; we view and accept
 * the invite over the same WebSocket the rest of the app uses.
 */
@Component({
  selector: 'app-share-accept',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  templateUrl: './share-accept.component.html',
  styleUrl: './share-accept.component.scss'
})
export class ShareAcceptComponent implements OnInit {
  token = '';
  info: any = null;
  loading = true;
  accepting = false;
  result: any = null;

  constructor(private route: ActivatedRoute,
              private router: Router,
              private ws: WebsocketService) {}

  async ngOnInit(): Promise<void> {
    this.token = this.route.snapshot.paramMap.get('token') || '';
    try { await this.ws.connect(); } catch { /* sendMessage will no-op; we show a timeout */ }

    this.ws.on('SHAREINFO').pipe(take(1)).subscribe((i: any) => {
      this.info = i; this.loading = false;
    });
    this.ws.on('SHAREACCEPTED').pipe(take(1)).subscribe((r: any) => {
      this.accepting = false; this.result = r;
      if (r?.ok) setTimeout(() => this.router.navigate(['/controller', r.mac]), 1200);
    });

    // The server rejects anything but HELLO until the session is authorized, so wait for
    // AUTHORIZED (fires immediately if we are already connected) before asking about the invite.
    this.ws.authorized$.pipe(filter(a => a), take(1)).subscribe(() => {
      this.ws.sendMessage({ type: 'SHAREINFO', payload: { token: this.token } });
    });
    // If nothing comes back (WS not up), stop the spinner so the user isn't stuck.
    setTimeout(() => { if (this.loading) this.loading = false; }, 8000);
  }

  accept(): void {
    this.accepting = true;
    this.ws.sendMessage({ type: 'SHAREACCEPT', payload: { token: this.token } });
  }

  home(): void { this.router.navigate(['/']); }

  levelKey(l: string): string {
    return l === 'FULL' ? 'share.level.full_long' : 'share.level.outputs';
  }

  /**
   * The server already speaks in codes here (not_found / already_used / expired), so the
   * wording is ours to pick. An unrecognised code falls back to the generic line.
   */
  get invalidReasonKey(): string {
    if (!this.info) return 'share.accept.err.fetch_failed';
    return this.reasonKey(this.info.reason, 'share.accept.err.not_found');
  }

  get failedReasonKey(): string {
    return this.reasonKey(this.result?.reason, 'share.accept.err.invalid');
  }

  private reasonKey(reason: string | undefined, fallback: string): string {
    switch (reason) {
      case 'already_used':    return 'share.accept.err.already_used';
      case 'expired':         return 'share.accept.err.expired';
      case 'expired_or_self': return 'share.accept.err.expired_or_self';
      case 'not_found':       return 'share.accept.err.not_found';
      default:                return fallback;
    }
  }
}
